/**
 * Delivery Timeline scope: one row per Parsec Partner, dots ordered by
 * created date. Only tasks created in the rolling 12-month window are
 * included. Tasks with an empty Type ("No Type" in the
 * warehouse) are excluded.
 */
export const DELIVERY_TIMELINE_TYPES = [
    "MMR/Growth/Status",
    "Platform Build",
    "Dashboard - Add From Template",
    "Filters - Custom",
    "Custom Dashboard"
];
const DELIVERY_TYPE_SET = new Set(DELIVERY_TIMELINE_TYPES);
export function isDeliveryTimelineType(type) {
    return DELIVERY_TYPE_SET.has(type);
}
export function buildDeliveryTimeline(asOf, partners, missingPartner = []) {
    const rows = partners
        .map((partner) => rollupPartner(asOf, partner.partner, partner.tasks))
        .filter((row) => row != null);
    rows.sort((a, b) => {
        if (b.blocked !== a.blocked)
            return b.blocked - a.blocked;
        if (b.late !== a.late)
            return b.late - a.late;
        if (b.open !== a.open)
            return b.open - a.open;
        if (a.earliestCreated !== b.earliestCreated)
            return a.earliestCreated.localeCompare(b.earliestCreated);
        return a.partner.localeCompare(b.partner);
    });
    const missing = missingPartner
        .filter((task) => isDeliveryTimelineType(task.type) && task.created)
        .map((task) => classifyTask(asOf, task))
        .sort((a, b) => a.created.localeCompare(b.created) || a.title.localeCompare(b.title) || a.id.localeCompare(b.id));
    return { asOf, rows, missingPartner: missing };
}
const UNTYPED = "No Type";
function rollupPartner(asOf, partner, tasks) {
    const untypedOpen = tasks.filter((task) => task.type === UNTYPED && task.completed == null).length;
    const classified = tasks
        .filter((task) => isDeliveryTimelineType(task.type) && task.created)
        .map((task) => classifyTask(asOf, task))
        .sort((a, b) => a.created.localeCompare(b.created) || a.id.localeCompare(b.id));
    const earliestTyped = classified[0];
    if (!earliestTyped && untypedOpen === 0)
        return null;
    let open = 0;
    let late = 0;
    let blocked = 0;
    let completed = 0;
    for (const task of classified) {
        if (task.status === "complete")
            completed += 1;
        else
            open += 1;
        if (task.status !== "complete" && task.due != null && task.due < asOf)
            late += 1;
        if (task.status === "blocked")
            blocked += 1;
    }
    const earliestUntyped = tasks
        .filter((task) => task.type === UNTYPED && task.created)
        .map((task) => task.created)
        .sort()[0];
    const timeline = partnerTimeline(asOf, classified);
    return {
        partner,
        earliestCreated: earliestTyped?.created ?? earliestUntyped ?? "",
        open,
        late,
        blocked,
        completed,
        total: classified.length,
        untypedOpen,
        timelineDays: timeline.timelineDays,
        timelineOpen: timeline.timelineOpen,
        timelineBlocked: timeline.timelineBlocked,
        timelineLate: timeline.timelineLate,
        tasks: classified
    };
}
const PLATFORM_BUILD = "Platform Build";
function partnerTimeline(asOf, tasks) {
    const builds = tasks.filter((task) => task.type === PLATFORM_BUILD);
    if (builds.length === 0)
        return { timelineDays: null, timelineOpen: false, timelineBlocked: false, timelineLate: false };
    let totalDays = 0;
    let open = false;
    let blocked = false;
    let late = false;
    for (const task of builds) {
        const end = task.completed ?? asOf;
        totalDays += Math.max(0, daysBetween(task.created, end));
        if (task.completed == null) {
            open = true;
            if (task.blocked)
                blocked = true;
        }
        if (task.due != null && end > task.due)
            late = true;
    }
    return {
        timelineDays: Math.round(totalDays / builds.length),
        timelineOpen: open,
        timelineBlocked: blocked,
        timelineLate: late
    };
}
function daysBetween(start, end) {
    const ms = Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`);
    return Math.round(ms / 86_400_000);
}
function classifyTask(asOf, task) {
    const completedLate = task.completed != null && task.due != null && task.completed > task.due;
    let status;
    if (task.completed != null)
        status = "complete";
    else if (task.blocked)
        status = "blocked";
    else if (task.due != null && task.due < asOf)
        status = "late";
    else
        status = "open";
    return { ...task, status, completedLate };
}
//# sourceMappingURL=deliveryTimeline.js.map